import stylesheet from './CustomComponent.scss?inline';

import {getSlotElements, getSlotHtml} from "../../util/slot";

export default abstract class CustomComponent extends HTMLElement {
    customStyle: string;
    private readonly styleElement: HTMLStyleElement;
    attributes: any;
    isAttached: boolean;
    passthroughEvents: string[] = [];

    protected constructor() {
        super()

        this.attachShadow({mode: 'open'});

        this.customStyle = "";
        const customComponentStyleElement = document.createElement("style")
        customComponentStyleElement.textContent = stylesheet;

        this.styleElement = document.createElement("style")
        this.styleElement.textContent = ""

        this.shadowRoot?.appendChild(customComponentStyleElement);
        this.shadowRoot?.appendChild(this.styleElement);

        this.isAttached = false;
    }

    // noinspection JSUnusedGlobalSymbols
    connectedCallback() {
        this.display()
    }

    display() {
        if (this.isAttached) {
            return;
        }
        this.styleElement.textContent = this.customStyle;

        let rendered = this.render()
        this.isAttached = true;
        this.shadowRoot?.append(rendered);
        if (this.shadowRoot) {
            this.attachEventPassthrough(this.shadowRoot);
        }
    }

    attachEventPassthrough(container: HTMLElement | ShadowRoot) {
        for (const eventType of this.passthroughEvents) {
            container.addEventListener(eventType, (ev) => {
                this.dispatchEvent(new Event(ev.type, ev));
            })
        }
    }

    getFilledSlotNames() {
        const slotNames = new Set(Array.from(this.querySelectorAll<HTMLElement>('[slot]')).map(el => el.slot));
        if (this.getSlotElements('').length > 0) {
            slotNames.add('');
        }
        return [...slotNames];
    }

    findTemplateSlot(templateElement: HTMLElement, slotName: string): HTMLElement | null {
        if (slotName === '') {
            return templateElement.querySelector(`slot:not([name])`);
        } else {
            return templateElement.querySelector(`slot[name="${slotName}"]`)
        }
    }

    setHasSlottedToParent(templateElement: HTMLElement, removeWhenAvailable = false) {
        for (const availableSlot of this.getFilledSlotNames()) {
            const slot = this.findTemplateSlot(templateElement, availableSlot);
            if (slot) {
                slot.parentElement.classList.add('has-slotted', `has-slotted-${availableSlot}`);
            } else if (!slot && removeWhenAvailable) {
                slot.parentElement.classList.remove('has-slotted', `has-slotted-${availableSlot}`);
            }
        }
    }

    abstract render(): HTMLElement;

    cloneDefaultSlotContentIntoContainer(containerElement: HTMLElement, removeIfEmpty = true) {
        this.cloneSlotContentIntoContainer(containerElement, '', removeIfEmpty);
    }

    getSlotElements(slotName = '') {
        return getSlotElements(this, slotName);
    }

    cloneSlotContentIntoContainer(containerElement: HTMLElement, slotToMove: string, removeContainerIfEmpty = true, removeSlotAttribute = true, addClasses: string[] = []) {
        const slotElements = this.getSlotElements(slotToMove);
        if (slotElements.length === 0 && removeContainerIfEmpty) {
            containerElement.remove();
        }
        slotElements.forEach(content => {
            if (removeSlotAttribute) {
                content.removeAttribute('slot');
            }
            content.classList.add(...addClasses);
            containerElement.appendChild(content);
        });
    }

    replaceDefaultSlotContents(newContent: HTMLElement[]) {
        this.getSlotElements('').forEach(el => el.remove());
        newContent.forEach(el => this.appendChild(el));
    }

    createElementWithSlots(tagName, slots: string[]) {
        const element = document.createElement(tagName);
        for (const slotName of slots) {
            this.cloneSlotContentIntoContainer(element, slotName, false, false);
        }
        return element;
    }

    async appendStyleLazy(lazyCallback: () => Promise<string>) {
        return lazyCallback().then(stylesheet => {
            this.styleElement.textContent += stylesheet;
        });
    }

    /**
     * Update contents of a named slot with the given Element
     *
     * @param responseMarkup Content to be set
     * @param slotName Slot name to be replaced (contents)
     */
    updateSlotIfExists(responseMarkup: HTMLElement, slotName = '') {
        const slotContainer = this.querySelector<HTMLElement>(`[slot="${slotName}"]`);
        const newHtml = getSlotHtml(responseMarkup, slotName);
        console.log(slotName, slotContainer, newHtml);
        if (newHtml) {
            slotContainer.innerHTML = getSlotHtml(responseMarkup, slotName);
        }
    }
}
